<template>
  <div class="base-card-info" :style="{padding: '16px', height: isSass ? 'calc(100% - 200px)' : 'calc(100% - 160px)'}">
    <div class="info-card-title">
      开票申请
      <el-tag>待开票发票科目数量：{{ invoiceList.length }}</el-tag>
    </div>
    <div v-if="invoiceList.length === 0" class="empty">
      <img src="@core/main/assets/images/tax-source-empty.png" alt="">
      <div>暂无子订单需要申请发票</div>
    </div>
    <template v-else>
      <content-container title="申请记录" :table-row-count="tableData.length" style="height: 100%; padding: 0;">
        <!-- 搜索条件区域 -->
        <search-container>
          <search-col>
            <el-select
              v-model="params.invoiceSubId"
              placeholder="订单开票科目"
              clearable
              filterable
              @clear="handleInvoiceSubIdClear"
              @change="handleInvoiceSubIdChange"
            >
              <el-option
                v-for="item in invoiceList"
                :key="item.invoiceSubId"
                :label="item.invoiceSubName"
                :value="item.invoiceSubId"
              />
            </el-select>
          </search-col>

          <search-col>
            <el-input v-model="params.freelancer" placeholder="自由职业者" clearable @keydown="onEnter" />
          </search-col>

          <search-col>
            <el-date-picker
              v-model="dateConfirmRange"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD HH:mm:ss"
              :default-time="defaultTimeFormat"
              style="width: 100%;"
              :disabled-date="disabledDate"
              type="daterange"
              range-separator="-"
              start-placeholder="完成时间(起)"
              end-placeholder="完成时间(止)"
            />
          </search-col>

          <template #buttons>
            <el-button @click="resetForm">
              <svg-icon icon="tabler:refresh" />重置
            </el-button>
            <el-button type="primary" @click="doSearch">
              <svg-icon icon="lucide:search" />查询
            </el-button>
          </template>
        </search-container>

        <!-- 非搜索区域的额外按钮 -->
        <div class="mb-5 flex items-center justify-between">
          <div class="flex items-center gap-x-2">
            <el-button type="primary" :disabled="ids.length === 0 && total === 0" @click="handleSelectAllBtn">
              {{ selectedIds.size == total && !isZero || selectedIds.size > total && !isZero ? '全不选' : '全选' }}
            </el-button>
            {{ selectedIds.size || 0 }} / {{ total }} 条
          </div>
          <div class="apply-btns">
            <template v-if="orderCheckResult?.warningOrderCount > 0">
              <div class="label warning-text !ml-3">
                异常订单数：
              </div>
              <div class="value warning-text !w-[40px]">
                {{ isNull(orderCheckResult?.warningOrderCount) ? '--' : orderCheckResult?.warningOrderCount }}
              </div>
              <div class="label !ml-0 warning-text">
                异常订单含税金额总计：
              </div>
              <div class="value warning-text">
                {{ isNull(orderCheckResult?.warningOrderAmount) ? '--' : orderCheckResult?.warningOrderAmount }}
              </div>
              <el-button v-permission="[`broker:invoice:exception`]" plain type="primary" @click="emit('handleException', params?.invoiceSubId)">
                处理异常
              </el-button>
            </template>
            <el-button
              v-permission="[`broker:tax:invoice:application`]"
              type="primary"
              :disabled="selectedIds.size === 0"
              @click="handleApply"
            >
              申请开票
            </el-button>
          </div>
        </div>

        <!-- 表格数据 -->
        <el-table ref="tableRef" v-loading="loading" row-key="orderDetailId" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData" @select="handleSelect" @select-all="handleSelectAll">
          <el-table-column align="center" type="selection" width="80" />
          <el-table-column align="left" show-overflow-tooltip prop="orderDetailNo" label="子订单编号" :min-width="180" />
          <el-table-column align="left" show-overflow-tooltip prop="personName" :formatter="(row) => isNull(row.personName) ? '--' : row.personName" label="自由职业者" :min-width="180" />
          <el-table-column align="left" show-overflow-tooltip prop="dutyName" label="任务名称" :min-width="180" />
          <el-table-column align="right" show-overflow-tooltip prop="taxAmount" label="含税金额" :min-width="150" />
          <el-table-column align="left" prop="orderStartTime" label="订单创建时间" :min-width="180" />
          <el-table-column align="left" prop="orderEndTime" label="订单完成时间" :min-width="180" />
        </el-table>

        <!-- 底部分页 -->
        <template #footer>
          <el-pagination
            v-model:current-page="params.page"
            v-model:page-size="params.size"
            :page-sizes="[defaultPageSize, 25, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            background
            @size-change="onSizeChange"
            @current-change="onCurrentPageChange"
          />
        </template>

        <!-- 众包开票申请弹框 -->
        <match-invoice-application
          :visible="showInvoiceApplication"
          :selected-ids="selectedIds"
          :company-id="companyId"
          :invoice-sub-id="params.invoiceSubId"
          @close="showInvoiceApplication = false"
          @confirm="handleConfirm"
        />
      </content-container>
    </template>
  </div>
</template>

<script setup lang='ts'>
import MatchInvoiceApplication from '../dialogs/MatchInvoiceApplication.vue';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  queryMaintenanceStatus,
  queryMatchInvoiceApply,
} from '@core/main';
import { getFomattedTimeRange, isNull } from '@core/tools';
import { SvgIcon } from '@vue3/components';
import { ElMessage, type TableInstance } from 'element-plus';
import { ICrowdInvoiceApplyItem, ICrowdInvoiceApplyParams, IInvoiceSubjectAndTaxItem } from '@core/main/types/crowd-source/invoice-pay-taxes';
import { computed, nextTick, PropType, reactive, ref, watch } from 'vue';
import { useUserStore } from '@core/main';
import { ESystemType } from '@core/api';

const props = defineProps({
  invoiceList: {
    type: Array as PropType<IInvoiceSubjectAndTaxItem[]>,
    default: () => [],
  },
  source: {
    type: String as PropType<'server' | 'company' | 'sass-crowd' | 'sass-match'>,
    default: 'server',
  },
  companyId: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(['handleException', 'confirm']);

const { state: userState } = useUserStore();

const defaultPageSize = 12;

const params = reactive<ICrowdInvoiceApplyParams>({
  page: 1,
  size: defaultPageSize,
  invoiceSubId: props.invoiceList?.[0]?.invoiceSubId,
});
const invoiceSubIdCopy = ref();
const tableData = ref<ICrowdInvoiceApplyItem[]>([]);
const loading = ref(false);
const showInvoiceApplication = ref(false);
const total = ref(0);
const tableRef = ref<TableInstance>();
const selectedIds = ref(new Set([]));
const dateConfirmRange = ref<string[]>([]);
const ids = ref<number[]>([]);

const disabledDate = (current: Date) => current.getTime() > Date.now();

const defaultTimeFormat = computed(() => getFomattedTimeRange());
const isZero = computed(() => selectedIds.value.size === 0 && total.value === 0);
const orderCheckResult = computed(() => props.invoiceList?.filter((v) => v.invoiceSubId === params.invoiceSubId)[0]?.orderCheckResult);
const isSass = computed(() => userState.identity.systemType === ESystemType.SAAS);

async function getMaintenanceStatus() {
  try {
    await queryMaintenanceStatus();
    return true;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function handleApply() {
  if (!await getMaintenanceStatus()) return;

  showInvoiceApplication.value = true;
}

function resetForm() {
  dateConfirmRange.value = [];

  for (const key in params) {
    if (!['page', 'size', 'companyId', 'invoiceSubId'].includes(key)) {
      params[key] = undefined;
    }
  }
}

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all, ids: idsData } = await queryMatchInvoiceApply({
      ...params,
      companyId: props.companyId,
      orderEndTimeBegin: dateConfirmRange.value ? dateConfirmRange.value[0] : undefined,
      orderEndTimeEnd: dateConfirmRange.value ? dateConfirmRange.value[1] : undefined,
    });

    tableData.value = data;
    total.value = all;
    ids.value = idsData;

    // 恢复当前页选中状态
    nextTick(() => restoreSelection());
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function handleInvoiceSubIdChange(val: number) {
  selectedIds.value = new Set([]);
  params.invoiceSubId = isNull(val) ? invoiceSubIdCopy.value : val;
  params.page = 1;
  params.size = defaultPageSize;

  fetchData();
}

function handleInvoiceSubIdClear(value) {
  if (value === invoiceSubIdCopy.value) return;

  params.invoiceSubId = props.invoiceList?.[0]?.invoiceSubId;
}

const handleSelect = (selection, row) => {
  if (selection.includes(row)) {
    selectedIds.value.add(row.orderDetailId);
  } else {
    selectedIds.value.delete(row.orderDetailId);
  }
};

const handleSelectAll = (selection) => {
  const currentPageIds = tableData.value.map((item) => item.orderDetailId);

  if (selection.length === 0) {
    currentPageIds.forEach((id) => selectedIds.value.delete(id));
  } else {
    currentPageIds.forEach((id) => selectedIds.value.add(id));
  }
};

const handleSelectAllBtn = () => {
  if (selectedIds.value.size >= total.value) {
    selectedIds.value = new Set([]);
  } else {
    selectedIds.value = new Set(ids.value);
  }
  restoreSelection();
};

function handleConfirm() {
  selectedIds.value = new Set([]);
  restoreSelection();

  emit('confirm');
}

const restoreSelection = () => {
  if (!tableRef.value) return;

  tableData.value.forEach((row: ICrowdInvoiceApplyItem) => {
    const isSelected = selectedIds.value.has(row.orderDetailId);

    tableRef.value.toggleRowSelection(row, isSelected);
  });
};

function doSearch() {
  params.page = 1;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

watch(
  () => props.invoiceList,
  (val) => {
    if (val && val?.length > 0) {
      selectedIds.value = new Set([]);
      params.invoiceSubId = val?.filter((v) => v.invoiceSubId === params.invoiceSubId)?.[0]?.invoiceSubId || val?.[0]?.invoiceSubId;
      invoiceSubIdCopy.value = JSON.parse(JSON.stringify(params.invoiceSubId));

      fetchData();
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.empty {
  @apply h-[calc(100%-36px)] w-full flex flex-col items-center justify-center gap-y-6 font-[16px] text-black;

  img {
    @apply w-[236px] h-[193px];
  }
}

.label {
  @apply ml-1 mr-[6px] text-[#595959] h-[25px] leading-[25px];
}

.value {
  @apply w-[70px] h-[25px] leading-[25px] text-left truncate text-[#262626] text-[14px];
}

.warning-text {
  @apply text-[#F98500];
}

.warning-text.value {
  @apply text-[12px];
}

.apply-btns {
  @apply flex items-center gap-x-1;

  .el-button.is-plain {
    --el-color-warning: var(--color-primary);
    --el-button-bg-color: #fff;
    --el-button-text-color: var(--color-primary);
    --el-button-border-color: var(--color-primary);
    --el-button-hover-bg-color: var(--color-primary);
    --el-button-hover-border-color: var(--color-primary);
    --el-button-hover-text-color: #fff;
    --el-button-active-bg-color: var(--color-primary-active);
    --el-button-active-border-color: var(--color-primary-active);
    --el-button-active-text-color: #fff;
  }
}
</style>
